import { Typography } from '@automata/ui';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { useState } from 'react';
import { Position } from 'reactflow';

import EmptyLabwareSection from './EmptyLabwareSection';
import LabwareInTask from './ExistingLabwareSection';
import {
  useActionTaskDataHandleComponents,
  useLabwareHandleComponents,
} from './utils';
import {
  DATA_INPUT,
  TIME_CONSTRAINT_HANDLE_END,
  TIME_CONSTRAINT_HANDLE_START,
} from '../../../../constants';
import { toTaskDependencyHandleId } from '../../../../features/helpers';
import type {
  InstrumentForLocation,
  LabwareDataForTaskByHandlePairId,
  TaskProperties,
} from '../../../../types';
import { getTaskHasDeadEndNode } from '../../../../utils';
import { Icon as InstrumentIcon } from '../../../customIcons/instruments';
import { Icon } from '../../../customIcons/tasks';
import { DataHandleWithLabel } from '../../DataHandle';
import Handle from '../../Handle';
import NodeBackground from '../../NodeBackground';
import TimeConstraintHandle from '../../TimeConstraintHandle';

type Props = {
  indicators: JSX.Element | null;
  instrumentData: InstrumentForLocation;
  labwareDataByHandle: LabwareDataForTaskByHandlePairId;
  orderedHandlePairIds: string[];
  selected: boolean;
  taskProperties: TaskProperties;
};

export const TaskNode = ({
  indicators,
  instrumentData,
  labwareDataByHandle,
  orderedHandlePairIds,
  selected,
  taskProperties,
}: Props): JSX.Element | null => {
  const [isHovered, setIsHovered] = useState(false);

  const hasDeadEnd = getTaskHasDeadEndNode(taskProperties.taskType);

  const labwareLabelledHandleComponents = useLabwareHandleComponents({
    instrumentCapacity: instrumentData.capacity,
    labwareDataByHandle,
    orderedHandlePairIds,
    taskId: taskProperties.id,
    taskType: taskProperties.taskType,
  });

  const { inputs, outputs } = useActionTaskDataHandleComponents({
    taskId: taskProperties.id,
  });

  const isBiomekSpecialCase =
    instrumentData.driverName === 'beckman_biomek' &&
    instrumentData.bench === 'reuben';

  const showOutputsBlock =
    !isBiomekSpecialCase ||
    Boolean(taskProperties.taskParameters?.generate_output_file);

  return (
    <Stack
      direction="row"
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
      pr={2} // this fixes issues where time constraint edge snaps before handle is visible on hover
    >
      {/* Label */}
      <Box
        textAlign="right"
        position="absolute"
        left={0}
        sx={{ transform: 'translate(-100%) translate(-0.5rem)' }}
        bgcolor="rgba(248, 248, 248, 0.8)"
        borderRadius={1}
        p={1}
      >
        <Typography variant="h6">
          {taskProperties.nickname ?? taskProperties.taskType}
        </Typography>
        <Typography variant="body2">{instrumentData.name}</Typography>

        {indicators}
      </Box>

      {/* Node box & contents */}
      <NodeBackground isSelected={selected}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          minHeight="2.5rem"
        >
          <Icon iconType={taskProperties.taskType} fontSize="large" />
          {instrumentData.driverName !== 'placeholder' && (
            <InstrumentIcon
              iconType={instrumentData.driverName}
              fontSize="large"
            />
          )}

          {!taskProperties.isLabwareLessTask &&
            instrumentData.driverName !== 'placeholder' && (
              <>
                <Divider orientation="vertical" flexItem />
                {labwareLabelledHandleComponents.map((data, i) => {
                  if (data.handleType === 'labware') {
                    return (
                      <LabwareInTask
                        key={i}
                        hasDeadEnd={hasDeadEnd}
                        handlePairId={data.handlePairId}
                        data={data.labwareData}
                      />
                    );
                  }
                  if (data.handleType === 'addLabware') {
                    return (
                      <EmptyLabwareSection
                        key={i}
                        hasDeadEnd={hasDeadEnd}
                        handlePairId={data.handlePairId}
                      />
                    );
                  }
                  data satisfies never;
                })}
              </>
            )}

          {inputs.length > 0 && <Divider orientation="vertical" flexItem />}
          {inputs.map(
            (data) =>
              data.handleType === DATA_INPUT && (
                <DataHandleWithLabel
                  key={data.handleId}
                  name={data.name}
                  id={data.handleId}
                  position={Position.Top}
                  handleStyle={{ top: -23 }}
                  isConnected={data.isConnected}
                />
              )
          )}
          {showOutputsBlock && outputs.length > 0 && (
            <Divider orientation="vertical" flexItem />
          )}
          {showOutputsBlock &&
            outputs.map((data) => (
              <DataHandleWithLabel
                key={data.handleId}
                name={data.name}
                id={data.handleId}
                position={Position.Bottom}
                handleStyle={{ bottom: -23 }}
                isConnected={data.isConnected}
              />
            ))}
        </Stack>
      </NodeBackground>

      {/* Time Constraint handles */}
      <Box sx={{ transform: `translate(1rem)` }}>
        <TimeConstraintHandle
          id={TIME_CONSTRAINT_HANDLE_START}
          taskId={taskProperties.id}
          style={{ top: '20%' }}
          nodeIsHovered={isHovered}
          nodeIsSelected={selected}
        />
        {!hasDeadEnd && (
          <TimeConstraintHandle
            id={TIME_CONSTRAINT_HANDLE_END}
            taskId={taskProperties.id}
            style={{ top: '80%' }}
            nodeIsHovered={isHovered}
            nodeIsSelected={selected}
          />
        )}
      </Box>

      <Box position="absolute" top="50%" left={40}>
        <Handle
          id={toTaskDependencyHandleId('source', taskProperties.id)}
          position={Position.Right}
          type="source"
          isConnectable={false}
          style={{ visibility: 'hidden' }}
        />
        <Handle
          id={toTaskDependencyHandleId('target', taskProperties.id)}
          position={Position.Right}
          type="source"
          isConnectable={false}
          style={{ visibility: 'hidden' }}
        />
      </Box>
    </Stack>
  );
};
